<template>
  <panel-layout
    id="resource-type-panel"
    :loading="loading"
    :shouldDismiss="() => !unsavedChanges"
    :didAttemptToDismiss="cancel"
  >
    <template #actions-right>
      <div v-if="resourceType && !resourceType.isNewRecord" class="flex items-center space-x-1">
        <o-history-link
          :item="resourceType"
          icon="history"
          slim
          flat
          model="ResourceType"
        />

        <o-tooltip :text="$t('general.delete')">
          <o-button icon="delete" flat @click="handleDelete" />
        </o-tooltip>
      </div>
    </template>

    <template #heading>
      <div v-if="resourceType" class="flex items-center space-x-2">
        <div>
          <h2 class="text-lg md:text-xl tracking-tight font-bold text-gray-800/85 leading-none dark:text-gray-100">
            {{ resourceType.isNewRecord ? $t('resource.newGroup') : resourceType.name }}
          </h2>
          <div v-if="!resourceType.isNewRecord" class="mt-1">
            <p class="text-gray-700 text-sm md:text-base dark:text-gray-200" v-html="$t('pages.resourceType.edit.id', { id: resourceType.idResourceType })" />
          </div>
        </div>
      </div>
    </template>

    <o-scroll class="pb-6">
      <div v-if="resourceType" class="p-4 mb-6">
        <o-field-group v-slot="{ status }" :label="$t('resource.groupName')" :name="$t('resource.groupName')" col>
          <text-field v-model="resourceType.name" :status="status" />
        </o-field-group>

        <div v-if="resources" class="mb-12">
          <label class="block text-sm text-gray-700 font-medium dark:text-gray-200" tabindex="0">
            <span>{{ $t('general.resources') }}</span>
          </label>
          <p class="block text-gray-600 text-sm mb-6 dark:text-gray-300">
            {{ $t('resource.partOfGroup') }}
          </p>

          <div class="p-3 bg-gray-50 rounded-md dark:bg-gray-900">
            <div
              v-for="resource in resources"
              :key="resource.idEmployee"
              class="w-full flex items-center justify-between mb-2 pb-2 border-b last:border-none last:mb-0 last:pb-0"
            >
              <p class="text-sm" :class="_value.includes(resource.idEmployee) ? 'text-gray-700 dark:text-gray-200' : 'text-gray-300 dark:text-gray-600'">
                {{ resource.displayName }}
              </p>
              <toggle-field v-model="_value" :value="resource.idEmployee" />
            </div>
          </div>
        </div>

        <div class="bg-gray-50 border rounded-md p-3 mb-6 dark:bg-gray-900">
          <div class="flex items-center justify-between class " :class="allowEmployees ? 'border-b pb-2 mb-6' : ''">
            <div>
              <h5 class="text-gray-700 font-medium dark:text-gray-200">
                {{ $t('pages.resourceType.edit.allowEmployees') }}
              </h5>
              <p class="text-sm text-gray-600 dark:text-gray-300">
                {{ $t('pages.resourceType.edit.addSecondaryEmployee') }}
              </p>
            </div>

            <toggle-field v-model="allowEmployees" />
          </div>

          <div v-if="allowEmployees">
            <div
              v-for="employee in employees"
              :key="employee.idEmployee"
              class="w-full flex items-center justify-between mb-2 pb-2 border-b last:border-none last:mb-0 last:pb-0"
            >
              <p class="text-sm" :class="_value.includes(employee.idEmployee) ? 'text-gray-700 dark:text-gray-200' : 'text-gray-300 dark:text-gray-600'">
                {{ employee.displayName }}
              </p>
              <toggle-field v-model="_value" :value="employee.idEmployee" />
            </div>
          </div>
        </div>
      </div>
    </o-scroll>

    <template #footer>
      <div v-if="resourceType">
        <div class="flex justify-between align-items">
          <div class="flex items-center space-x-1">
            <o-button outline flat class="mr-2" @click="cancel()">
              {{ $t('general.cancel') }}
            </o-button>
          </div>
          <div class="flex items-center space-x-1">
            <o-button variant="primary" outline icon="save" class="mr-2" @click="save()">
              {{ $t('general.save') }}
            </o-button>
            <o-button variant="primary" icon="save" class="mr-2" @click="save(true)">
              {{ $t('general.saveAndClose') }}
            </o-button>
          </div>
        </div>
      </div>
    </template>

    <delete-dialog
      ref="deleteDialog"
      @success="cancel(true)"
    />

    <o-dialog
      ref="confirmDialog"
      :title="$t('general.unsavedChanges')"
      :info="$t('general.unsavedChangesInfo')"
      :options="[
        { text: $t('general.back'), type: 'cancel' },
        { text: $t('general.closeWithoutSaving'), type: 'confirm', variant: undefined, flat: true, outline: true, callback: () => cancel(true) },
        { text: $t('general.saveAndClose'), type: 'confirm', response: 'save', callback: () => save(true) }
      ]"
      :portal="panel.portal"
    />
  </panel-layout>
</template>
<script setup lang="ts">
// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'
import DeleteDialog from '@/components/resourceType/actions/Delete.vue'

import {
  OFieldGroup,
} from '@/components/elements'

import {
  TextField,
  ToggleField,
} from '@/components/fields'

import { usePanel } from '~/composables/usePanel'

const panel = usePanel()

const props = defineProps<{ id?: string }>()

const { $api, $notification, $bus, $i18n } = useNuxtApp()

const employeeStore = useEmployeeStore()

const resourceType = ref<any>(null)
const errors = ref<any>(false)
const loading = ref(true)
const allowEmployees = ref(false)

const confirmDialog = ref<any>()
const deleteDialog = ref<any>()

const resources = computed(() => employeeStore.employeesTypeResource)
const employees = computed(() => employeeStore.employeesTypeEmployee)

const _value = computed({
  get () {
    return resourceType.value?.resources
      .map(({ idEmployee }: any) => idEmployee) || []
  },
  set (value: any) {
    resourceType.value.resources = [...resources.value, ...employees.value]
      .filter(({ idEmployee }: any) => value.includes(idEmployee))
  },
})

const { unsavedChanges, snapshot } = useUnsavedChanges(resourceType)

watch(allowEmployees, (val) => {
  if (!val) {
    const ids = employees.value.map(({ idEmployee }: any) => idEmployee)

    const value = _value.value
      .filter((resource: any) => {
        return !ids.includes(resource)
      })

    _value.value = value
  }
})

const handleDelete = () => {
  deleteDialog.value.open(resourceType.value)
}

const save = async (close = false) => {
  loading.value = true
  errors.value = false

  try {
    const _resourceType = (await $api.resourceType.save(resourceType.value)).data

    if (_resourceType) {
      resourceType.value = _resourceType
      snapshot()

      $notification.toast({
        title: $i18n.t('pages.resourceType.edit.success'),
        text: $i18n.t('pages.resourceType.edit.successInfo'),
        variant: 'success',
      })

      $bus.emit.updated('ResourceType', _resourceType)

      if (close) {
        panel.close()
      }
    }
  } catch (e: any) {
    errors.value = e?.errors
    return false
  }

  loading.value = false
}

const cancel = (force = false) => {
  if (!force && unsavedChanges.value) {
    confirmDialog.value.open()
  } else {
    panel.close(force)
  }
}

async function init(id?: string) {
  loading.value = true
  try {
    if (!id) {
      resourceType.value = (await $api.resourceType.defaults({} as any)).data
    } else {
      resourceType.value = (await $api.resourceType.get(id)).data

      const _employees = resourceType.value?.resources
        .filter((employee: any) => employee.isEmployeeType) || []

      allowEmployees.value = !!_employees.length
    }
    snapshot()
  } catch (e) {
    console.log(e)
  }
  loading.value = false
}

watch(() => props.id, (id) => init(id), { immediate: true })
</script>
